import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { Star } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { paymentServiceApi } from '@/services/api';
import { useEffect, useState } from 'react';
import { analyticsService } from '@/services/analyticsService';

interface PremiumSubscriptionProps {
  className?: string;
}

interface PlanProps {
  id: string;
  title: string;
  price: string;
  originalPrice?: string | null;
  onSelect: (id: string) => void;
  isPopular?: boolean;
  discountPercent?: number;
}

interface SubscriptionPlan {
  plan_name: string;
  discount_price: string | null;
  duration_unit: 'month' | 'day' | 'year';
  duration_value: number;
  price: string;
  plan_id: number;
}

/* ─── Animated dots tagline ─────────────────────────────────────────── */
const AnimatedTagline = () => {
  const [visible, setVisible] = useState([false, false, false]);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const cycle = () => {
      [0, 1, 2].forEach((i) => {
        timers.push(
          setTimeout(() => {
            setVisible((prev) => {
              const next = [...prev];
              next[i] = true;
              return next;
            });
          }, i * 350)
        );
      });
      timers.push(
        setTimeout(() => setVisible([false, false, false]), 1800)
      );
    };

    cycle();
    const interval = setInterval(cycle, 2800);
    return () => {
      timers.forEach(clearTimeout);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="flex items-center justify-center gap-2.5 py-6 pb-2">
      <p className="text-xl font-extrabold tracking-tight text-center text-black">
        بوکا، لذت تعامل با کتاب‌ها
      </p>
      <div className="flex items-center gap-1 flex-shrink-0">
        {visible.map((show, i) => (
          <span
            key={i}
            style={{
              opacity: show ? 1 : 0,
              transition: 'opacity 0.4s ease',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.8)',
              display: 'inline-block',
            }}
          />
        ))}
      </div>
    </div>
  );
};

/* ─── Plan Card ─────────────────────────────────────────────────────── */
const Plan = ({ id, title, price, originalPrice, onSelect, isPopular = false, discountPercent = 0 }: PlanProps) => (
  <div
    onClick={() => onSelect(id)}
    className={cn(
      'p-4 cursor-pointer transition-all relative min-h-[96px] flex flex-col justify-center',
      'active:scale-[0.98] ring-1 ring-black/10 rounded-3xl',
      isPopular
        ? 'scale-[1.07] hover:scale-[1.08] bg-[#6A5BE2] text-white ring-2 ring-[#6A5BE2]/30'
        : 'scale-[0.98] hover:scale-100 bg-[#FFFDFC]'
    )}
    style={{
      background: isPopular ? '#6A5BE2' : '#FFFDFC',
      boxShadow: isPopular
        ? '0 14px 26px -8px rgba(106, 91, 226, 0.8), 0 0 6px 1px rgba(0, 0, 0, 0.16)'
        : '0 0 6px 1px rgba(0, 0, 0, 0.16)',
    }}
  >
    {isPopular && (
      <div className="absolute -top-2 right-6 select-none z-10">
        <div className="bg-orange-500 text-white px-2 py-0.5 rounded-b-md shadow flex items-center gap-0.5">
          <Star className="w-2.5 h-2.5 fill-current" />
          <span className="text-[11px] font-medium">پیشنهادی</span>
        </div>
      </div>
    )}

    {discountPercent > 0 && (
      <div className="absolute -top-2 left-3 rounded-lg bg-red-500 px-2 py-1 text-[9px] font-bold leading-none text-white shadow-sm">
        <bdi dir="rtl" className="inline-block">
          {discountPercent.toLocaleString('fa-IR')} %
        </bdi>{' '}تخفیف
      </div>
    )}

    <div className="flex justify-between items-center relative z-20">
      <h3 className={cn('font-bold text-lg', isPopular ? 'text-white' : 'text-gray-800')}>
        {title}
      </h3>

      <div className="flex items-center gap-3">
        <div className="text-left">
          {originalPrice ? (
            <div className="flex flex-col items-end">
              <span className={cn('font-extrabold text-xl', isPopular ? 'text-white' : 'text-black')}>
                {price}
              </span>
              <span className={cn('text-xs line-through', isPopular ? 'text-white/60' : 'text-gray-400')}>
                {originalPrice}
              </span>
            </div>
          ) : (
            <p className={cn('font-bold text-xl', isPopular ? 'text-white' : 'text-gray-900')}>{price}</p>
          )}
        </div>
        <svg
          className={cn('w-6 h-6 flex-shrink-0', isPopular ? 'text-white' : 'text-gray-400')}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="2.5"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </div>
    </div>
  </div>
);

/* ─── Main Component ─────────────────────────────────────────────────── */
const PremiumSubscription = ({ className }: PremiumSubscriptionProps) => {
  const navigate = useNavigate();

  const handleGoBack = () => navigate(-1);

  const handlePlanSelect = (plan: SubscriptionPlan) => {
    void analyticsService.logEvent('select_subscription_plan', {
      plan_id: String(plan.plan_id),
      plan_name: plan.plan_name,
      payment_type: 'subscription',
      duration_unit: plan.duration_unit,
      duration_value: plan.duration_value,
    });

    navigate('/payment-confirmation', {
      state: {
        planId: plan.plan_id,
        paymentType: 'subscription',
        planData: {
          plan_name: plan.plan_name,
          price: plan.price,
          discount_price: plan.discount_price,
          duration_unit: plan.duration_unit,
          duration_value: plan.duration_value,
        },
      },
    });
  };

  const formatPrice = (value: string | null): string => {
    if (!value) return '';
    const num = Number.parseFloat(value);
    if (Number.isNaN(num)) return value;
    return `${num.toLocaleString('fa-IR')} تومان`;
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async (): Promise<SubscriptionPlan[]> => {
      const res = await paymentServiceApi.get<SubscriptionPlan[]>('/subscription-plans/');
      if (!res.success) throw new Error(res.error || 'Failed to load plans');
      return res.data;
    },
  });

  useEffect(() => {
    if (Array.isArray(data)) {
      void analyticsService.logEvent('view_subscription_plans', {
        plan_count: data.length,
        payment_type: 'subscription',
      });
    }
  }, [data]);

  return (
    <div className={cn('min-h-screen font-persian relative overflow-hidden bg-white', className)}>
      
      {/* ── Header (part of the page, not sticky) ── */}
      <div className="px-4 pt-10 pb-2 flex items-center justify-between relative z-10">
        <button
          onClick={handleGoBack}
          className="p-2 rounded-full transition-colors"
          style={{ background: 'rgba(100,100,160,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(100,100,160,0.15)' }}
          aria-label="بازگشت"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <h1 className="text-base font-bold text-gray-800">اشتراک بوکا</h1>
        <div className="w-9" />
      </div>

      <main className="px-4 pb-24 max-w-xl mx-auto relative z-10">

        {/* ── Animated tagline ── */}
        <AnimatedTagline />

        {/* ── Plans ── */}
        <div className="mt-4 space-y-[18px]">
          {isLoading && (
            <div className="text-center text-gray-500 py-4">در حال بارگذاری پلن‌ها...</div>
          )}
          {isError && (
            <div className="text-center text-red-500 py-4">خطا در بارگذاری. لطفاً دوباره تلاش کنید.</div>
          )}
          {!isLoading && !isError && Array.isArray(data) &&
            data.map((p) => {
              const hasDiscount = p.discount_price !== null;
              const isPopular = p.duration_unit === 'month' && p.duration_value === 3;
              const originalPrice = Number(p.price);
              const finalPrice = Number(hasDiscount ? p.discount_price : p.price);
              const discountPercent = hasDiscount && originalPrice > 0
                ? Math.round((1 - (finalPrice / originalPrice)) * 100)
                : 0;
              return (
                <Plan
                  key={p.plan_id}
                  id={String(p.plan_id)}
                  title={p.plan_name}
                  price={formatPrice(hasDiscount ? p.discount_price : p.price)}
                  originalPrice={hasDiscount ? formatPrice(p.price) : undefined}
                  onSelect={() => handlePlanSelect(p)}
                  isPopular={isPopular}
                  discountPercent={discountPercent}
                />
              );
            })}
        </div>

      </main>
    </div>
  );
};

export default PremiumSubscription;
